import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSolutionOrg } from '../../config/useSolutionOrg';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectBusinessInformationQuestions,
  selectGetOnboardingFormStatus,
} from '../../store/slices/onboardingSlice';
import { getOnboardingForm } from '../../store/thunks/onboardingThunk';
import { QuestionOption } from '../models/Question';
import { useCustomEventTracking } from '../useCustomEventTracking';

export const BUSINESS_TRADE_QUESTION_KEY = 'businessMainIndustry';

/**
 * The trades a member can pick from: the options of the onboarding form's business trade
 * question, the same list the Preferences page offers. Loads the form when it is not in the
 * store yet. `isLoadingTradeOptions` is true until the first options are in or the request has
 * settled, so a picker is never built from an empty list that fills in later. If the request has
 * settled, failed or not, and there are still no options (for example the form has no trade
 * question), `hasTradeOptionsError` is true until `retryTradeOptions` loads the form again.
 */
export const useBusinessTradeOptions = (): {
  tradeOptions: QuestionOption[];
  isLoadingTradeOptions: boolean;
  hasTradeOptionsError: boolean;
  retryTradeOptions: () => void;
} => {
  const dispatch = useAppDispatch();
  const questions = useAppSelector(selectBusinessInformationQuestions);
  const formStatus = useAppSelector(selectGetOnboardingFormStatus);
  const { envSolutionOrgFormId } = useSolutionOrg();
  const { trackRequest } = useCustomEventTracking();
  const [requestSettled, setRequestSettled] = useState(false);

  const options = useMemo(
    () => questions.find(
      (question) => question.meta?.memberProperty === BUSINESS_TRADE_QUESTION_KEY,
    )?.options ?? [],
    [questions],
  );

  const loadForm = useCallback(() => {
    setRequestSettled(false);
    dispatch(getOnboardingForm({
      formId: (envSolutionOrgFormId as string) || '',
      trackRequest,
    }))
      .then(unwrapResult)
      .catch(() => undefined)
      .then(() => setRequestSettled(true));
  }, [dispatch, envSolutionOrgFormId, trackRequest]);

  useEffect(() => {
    if (options.length === 0) loadForm();
    // Only needs to run when the page opens: the form is fetched once and then shared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    tradeOptions: options,
    isLoadingTradeOptions: formStatus === 'pending' && options.length === 0,
    hasTradeOptionsError: requestSettled && options.length === 0,
    retryTradeOptions: loadForm,
  };
};
